/**
 * Action bar (bottom center) — 12 ability slots (#322, bumped from 8 — 8 was
 * too small for the shipped Priest kit, 11 abilities, #334) plus a distinct,
 * always-leftmost auto-attack icon (13 icons total).
 *
 * Each ABILITY slot renders:
 *   • its keybind (top-left, from the keymap — "1".."8" for slots 0-7,
 *     q/e/r/` for slots 8-11 by default, #407; the unbound-fallback 1-based
 *     slot number only shows if a player clears a binding entirely — see
 *     `DEFAULT_CAST_SLOTS` in `keymap.ts`)
 *   • a placeholder "icon": the first letter of the ability name
 *     (from the client ability-label table; empty for reserved slots)
 *   • the per-slot cast/cooldown/gcd sweep + rejection flash
 *   • (#308) a translucent darken overlay + numeric seconds-remaining
 *     readout while on cooldown, and a brief "ready" pop when the
 *     cooldown ends
 *   • (#466) for a CHARGE-based ability (`charges` in content): the SAME
 *     darken overlay while 0 charges are available ("cooldown" state,
 *     visually identical to an ordinary on-cooldown ability), a LIGHTER
 *     version of it while >= 1 charge is available but the pool isn't full
 *     ("charging" state — the sweep tracks the NEXT charge's recharge, not
 *     `cooldownTicks`), and a bottom-right white charge-count readout
 *     (any state) for any ability with a `charges` block. See
 *     `computeActionBarState`'s #466 branch + `CooldownOverlayStyle`.
 *
 * The AUTO-ATTACK icon (leftmost, `AutoAttackSlot`) is deliberately NOT an
 * ability slot: it has no keybind badge, no cast/cooldown sweep (the wire
 * carries no auto-attack swing-timer state to the client — `Entity.
 * autoCastState` in `@pit/sim` is server/replay-internal only), and a
 * visually distinct border so it never gets confused with the numbered
 * slots. It is purely a "this champion also auto-attacks" indicator.
 *
 * Per-ability-slot state, from the wp7 spec:
 *   • ready       — no cast, no cooldown, no gcd
 *   • cooldown    — cooldownReadyAtTick > latestTick
 *   • gcd         — gcdReadyAtTick > latestTick
 *   • casting     — cast.slot === i && cast.startTick..endTick around latestTick
 *
 * "Rejection feedback" — the keypress local-flash: reducer stashes
 * `castFlashes` with a ts; the ActionBar reads them and renders a red
 * outline for the flash lifetime (150ms managed by the reducer).
 *
 * data-* attributes used by WP8 e2e (per ability slot):
 *   • data-state       — "ready" | "cooldown" | "gcd" | "casting" | "charging" (#466)
 *   • data-slot        — "0".."11"
 *   • data-fraction    — 0..1 sweep progress (cooldown/gcd/casting/charging)
 *   • data-flash       — "true" when the local red-flash is active
 *   • data-just-ready  — "true" for the #308 "just became ready" pop window
 *   • data-range-state — "neutral" | "inRange" | "outOfRange" (#352)
 * testid `action-slot-0` remains stable for existing e2e selectors; the
 * auto-attack icon's testid is `action-slot-auto` (never numeric, so it can
 * never collide with a real ability slot index). #466 adds
 * `action-slot-<n>-charge-count` (present only for a `charges` ability) and
 * a `data-charge-shade` ("full" | "partial") attribute on the existing
 * `action-slot-<n>-cooldown-overlay` testid distinguishing the two overlay
 * shades.
 *
 * #339: hovering a filled ability slot shows a tooltip (name + the
 * ability's authored `description` — see `abilityFacts.ts`, the single
 * auto-derived source for both description and raw-facts, #461) plus a
 * raw-facts block. A reserved/empty slot
 * shows no tooltip (nothing to describe). testid `action-slot-<n>-tooltip`,
 * only present in the DOM while hovered. The tooltip itself (`AbilityTooltip`,
 * #410) is shared with `LoadoutPicker`'s hover tooltips, not private to this
 * file.
 *
 * #352: the keybind badge (top-left) is colored by whether the ability is
 * currently in RANGE:
 *   • neutral grey (`#8a93a0`, the pre-#352 default) — no relevant target/aim
 *     to check yet (empty slot, "self"/"none"-target ability, or an
 *     "enemy"/"ally"/"ground" ability with no target selected / cursor not
 *     yet known).
 *   • white (`#f2f4f8`) — a target/aim IS known and within `rangeUnits`.
 *   • dark red (`#8b1e1e`) — a target/aim IS known but OUTSIDE `rangeUnits`.
 * A "ground" (skillshot) ability checks the CURSOR's last known world
 * position (`cursorTracker.ts`, written by `InputSurface` on every pointer
 * move) rather than a locked-on target — see `computeRangeState`.
 * `data-range-state` on the slot carries this for e2e/tests.
 *
 * #415: clicking a FILLED slot casts that ability — a fallback alongside the
 * keybind, on by default, reusing `mapper.ts`'s `actionToIntent({ kind:
 * "castSlot", slot }, ctx)` (the exact same translation a keypress goes
 * through) so aim/gating semantics never diverge between the two input
 * paths. An empty/reserved slot's click is a no-op (nothing to cast).
 */

import { useRef, useState, type ReactElement } from "react";
import type { ChampionId, WireSimState } from "@pit/protocol";
import { toAbilitySlot } from "@pit/protocol";
import { useStore } from "../store/context.js";
import { useWsClient } from "../net/WsClientProvider.js";
import { ownEntity, targetEntity, friendlyTargetEntity, ticksToSeconds } from "./hud-util.js";
import { castSlotKeyLabel, useKeymap } from "../input/keybinding/index.js";
import { actionToIntent, mapResultInputs, type MapperContext } from "../input/mapper.js";
import { getLastCanvasPoint } from "../input/cursorTracker.js";
import {
  ACTION_BAR_BOTTOM_INSET_PX,
  ACTION_BAR_SLOT_PX,
  NO_OFFSET,
  screenToWorld,
  type ProjectionCenter,
} from "../render/index.js";
import {
  ACTION_SLOT_COUNT,
  DEFAULT_HUD_CHAMPION_ID,
  abilityIconLetterResolved,
  abilityLabelResolved,
} from "../champions/abilityLabels.js";
import { abilityIconSrc } from "../champions/abilityIcons.js";
import { abilityDescription, abilityFacts } from "../champions/abilityFacts.js";
import { abilityTargetType } from "../champions/abilityTargetType.js";
import { AbilityTooltip } from "../champions/AbilityTooltip.js";
import { resolveVisualSlotToCanonical } from "../champions/abilityLayout.js";
import { reportCastError } from "../input/castability.js";
import { cooldownGateTick, gcdGateTick } from "../input/castReadiness.js";

const TICK_RATE = 30;

/**
 * #466: all cooldown-overlay visual knobs, named — mirrors `combatText.ts`'s
 * `CombatTextStyle` discipline ("every visual knob is a named constant,
 * never a literal buried in the draw path"). The ordinary cooldown overlay
 * (`darkOverlay`) is UNCHANGED from its pre-#466 literal
 * (`rgba(0,0,0,0.55)`) — this only NAMES it and adds the sibling
 * `partialChargeOverlay` treatment.
 */
export interface CooldownOverlayStyle {
  /** Full-dark overlay — an ordinary ability on cooldown, OR a charge-based
   *  ability with 0 charges available (the "cooldown" state — see
   *  `computeActionBarState`). */
  readonly darkOverlay: string;
  /** Lighter overlay — a charge-based ability with >= 1 charge available but
   *  not yet at max (the "charging" state) — visibly distinct from the
   *  fully-dark "0 charges" treatment above. */
  readonly partialChargeOverlay: string;
  /** Charge-count readout color. */
  readonly chargeCountColor: string;
  /** Charge-count readout font size, px. Must read visibly LARGER than the
   *  keybind badge's `fontSize: 10` in a side-by-side comparison. */
  readonly chargeCountFontSizePx: number;
}

/** Default treatment — the single source of truth for every draw site below. */
export const DEFAULT_COOLDOWN_OVERLAY_STYLE: CooldownOverlayStyle = {
  darkOverlay: "rgba(0,0,0,0.55)",
  partialChargeOverlay: "rgba(0,0,0,0.24)",
  chargeCountColor: "#ffffff",
  chargeCountFontSizePx: 13,
};

/** #308 "just became ready" pop cue lifetime, ms. */
export const READY_POP_MS = 500;

export interface ActionBarState {
  /** #466: `"charging"` is a charge-based ability with >= 1 charge
   *  available but not yet at its max — visually distinct (lighter overlay)
   *  from `"cooldown"`, which now ALSO covers a charge-based ability with 0
   *  charges available (reads identically to an ordinary fully-on-cooldown
   *  ability, per the #466 spec). */
  readonly state: "ready" | "cooldown" | "gcd" | "casting" | "charging";
  readonly fraction: number;
  readonly label: string;
  /** Ceil'd seconds remaining until ready (#308) — nonzero ONLY in the
   *  "cooldown"/"charging" states; the numeric overlay reads this directly. */
  readonly remainingSeconds: number;
  /** #466: live available-charge count for a charge-based ability slot —
   *  mirrors the wire `cooldowns[].charges.available`. Populated in EVERY
   *  state (casting/gcd/cooldown/charging/ready), so the charge-count
   *  readout can render regardless of which overlay is currently showing.
   *  `undefined` for a slot whose ability has no `charges` block. */
  readonly chargesAvailable?: number;
  /** #466: the ability's static charge ceiling (`abilityFacts.ts`'s
   *  `maxCharges`) — gates whether the charge-count readout renders at all
   *  (`maxCharges !== undefined`). */
  readonly maxCharges?: number;
}

/**
 * Compute action-bar state for slot 0 from a snapshot (or null =
 * disconnected). PURE — the reducer of the whole action bar. Exposed
 * so the component test can drive it with fixture snapshots directly.
 *
 * `championId` (#308) resolves the ability's REAL cooldown duration from
 * `abilityFacts.ts`'s auto-derived `cooldownTicks` (#461) — defaults to
 * `DEFAULT_HUD_CHAMPION_ID` so existing 3-arg call sites/tests are
 * unaffected.
 */
export function computeActionBarState(
  snapshot: WireSimState | null,
  ownSeat: number | null,
  slot: number,
  championId: ChampionId = DEFAULT_HUD_CHAMPION_ID,
): ActionBarState {
  const me = ownEntity(snapshot, ownSeat);
  if (me === null || snapshot === null) {
    return { state: "ready", fraction: 0, label: "", remainingSeconds: 0 };
  }
  const tick = snapshot.tick;
  const facts = abilityFacts(championId, slot);
  const maxCharges = facts?.maxCharges;
  const cd = me.cooldowns.find((c) => c.slot === slot);
  // #466: live charge count, threaded onto EVERY return below (independent
  // of which branch actually fires) so the bottom-right readout renders in
  // every state, not just while the charge-recharge sweep is active.
  // `exactOptionalPropertyTypes` forbids assigning `undefined` directly to
  // an optional field, hence the conditional-spread `chargeFields` (mirrors
  // `abilityFacts.ts`'s own `...(x !== undefined ? { x } : {})` convention).
  const chargesAvailable = cd?.charges?.available;
  const chargeFields = {
    ...(chargesAvailable !== undefined ? { chargesAvailable } : {}),
    ...(maxCharges !== undefined ? { maxCharges } : {}),
  };

  const cast = me.cast;
  if (cast !== null && cast.slot === slot) {
    const total = cast.endTick - cast.startTick;
    const elapsed = tick - cast.startTick;
    const f = total > 0 ? clamp01(elapsed / total) : 1;
    return { state: "casting", fraction: f, label: "", remainingSeconds: 0, ...chargeFields };
  }

  // #466: a charge-based ability with FEWER than max charges available
  // overrides the ordinary single-cooldown/GCD branches below — its
  // readiness is governed by the charge pool, not `cooldownReadyAt`
  // (crossValidate pins `cooldownTicks === 0` for a charged ability, so the
  // plain `cd.readyAtTick > tick` branch never fires for these slots
  // anyway). The sweep fraction tracks the NEXT charge's recharge
  // (`nextChargeReadyAtTick`) against the ability's `rechargeTicks` — the
  // exact same "wire timestamp + content-derived duration" combination the
  // ordinary cooldown branch below already uses for `cooldownTicks`.
  // `available <= 0` reads as the "cooldown" state (same dark overlay as an
  // ordinary fully-on-cooldown ability); `0 < available < max` reads as the
  // new "charging" state (lighter overlay) — see the file header spec.
  if (
    cd !== undefined &&
    cd.charges !== null &&
    maxCharges !== undefined &&
    cd.charges.available < maxCharges
  ) {
    const { available, nextChargeReadyAtTick } = cd.charges;
    const remaining = nextChargeReadyAtTick - tick;
    const totalRechargeTicks = facts?.rechargeTicks ?? 0;
    const f = totalRechargeTicks > 0 ? 1 - clamp01(remaining / totalRechargeTicks) : 1;
    return {
      state: available <= 0 ? "cooldown" : "charging",
      fraction: f,
      label: "",
      remainingSeconds: ticksToSeconds(nextChargeReadyAtTick, tick, TICK_RATE),
      chargesAvailable: available,
      maxCharges,
    };
  }

  // #797: the cooldown/charge gate tick comes from the SHARED timing helper —
  // the same one the spell queue predicts its fire tick with, so the sweep the
  // player watches and the tick a queued cast fires on can never disagree.
  const cdGateTick = cooldownGateTick(cd);
  if (cdGateTick > tick) {
    const remaining = cdGateTick - tick;
    // #308: normalize the sweep against the ability's REAL cooldown
    // duration — was a hardcoded 60-tick/2s reference window, wrong for any
    // ability whose real cooldown differs (e.g. Warrior's Charge is 300
    // ticks/10s). The 0-total guard mirrors the casting branch above — a
    // real ability with cooldownTicks 0 never actually reaches this branch
    // (its readyAtTick is never ahead of `tick` for more than an instant),
    // but the function stays total.
    const totalCooldownTicks = facts?.cooldownTicks ?? 0;
    const f = totalCooldownTicks > 0 ? 1 - clamp01(remaining / totalCooldownTicks) : 1;
    return {
      state: "cooldown",
      fraction: f,
      label: "",
      remainingSeconds: ticksToSeconds(cdGateTick, tick, TICK_RATE),
      ...chargeFields,
    };
  }
  // #797: same shared helper for the GCD gate (see `cooldownGateTick` above).
  const gcdTick = gcdGateTick(me);
  if (gcdTick > tick) {
    const remaining = gcdTick - tick;
    // GCD is short (~30 ticks) — reference against 30 ticks.
    const f = 1 - clamp01(remaining / 30);
    return { state: "gcd", fraction: f, label: "", remainingSeconds: 0, ...chargeFields };
  }
  return { state: "ready", fraction: 0, label: "", remainingSeconds: 0, ...chargeFields };
}

function clamp01(x: number): number {
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

/** #352 range-indicator states — see the file header for the color mapping. */
export type RangeState = "neutral" | "inRange" | "outOfRange";

/** Keybind badge colors per {@link RangeState}. */
export const RANGE_STATE_COLOR: Readonly<Record<RangeState, string>> = {
  neutral: "#8a93a0",
  inRange: "#f2f4f8",
  outOfRange: "#8b1e1e",
};

/** A world-space point, unbranded (presentation-only — see `projection.ts`'s
 *  float-boundary doctrine; never re-enters protocol/sim types). */
export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Compute the #352 range state for `slot` on `championId`. PURE — the
 * pure-function twin of `computeActionBarState`, exposed so tests can drive
 * it with fixture snapshots directly.
 *
 * `cursorWorld` is the last known CURSOR world position (`null` if unknown)
 * — only consulted for a "ground" (skillshot) targetType; entity-targeted
 * abilities ("enemy"/"ally") instead check the caster's current
 * hostile/friendly target.
 */
export function computeRangeState(
  snapshot: WireSimState | null,
  ownSeat: number | null,
  slot: number,
  championId: ChampionId,
  cursorWorld: WorldPoint | null,
): RangeState {
  const me = ownEntity(snapshot, ownSeat);
  if (me === null) return "neutral";
  const targetType = abilityTargetType(championId, slot);
  if (targetType === null) return "neutral";
  const rangeUnits = abilityFacts(championId, slot)?.rangeUnits;
  if (rangeUnits === undefined) return "neutral";

  let aimPoint: WorldPoint | null;
  switch (targetType) {
    case "enemy": {
      const t = targetEntity(snapshot, ownSeat);
      aimPoint = t === null ? null : { x: t.x, y: t.y };
      break;
    }
    case "ally": {
      const t = friendlyTargetEntity(snapshot, ownSeat);
      aimPoint = t === null ? null : { x: t.x, y: t.y };
      break;
    }
    case "ground":
      aimPoint = cursorWorld;
      break;
    case "self":
    case "none":
      return "neutral";
    default:
      return "neutral";
  }
  if (aimPoint === null) return "neutral";

  const dx = aimPoint.x - me.x;
  const dy = aimPoint.y - me.y;
  const d2 = dx * dx + dy * dy;
  return d2 <= rangeUnits * rangeUnits ? "inRange" : "outOfRange";
}

/** Render a single ability slot. */
function ActionSlot({
  slot,
  cursorWorld,
  getProjectionCenter,
}: {
  /** The VISUAL bar position (0..11) — what the keybind badge, testid, and
   *  click handler's outgoing keybind resolution key off. STABLE identity
   *  for e2e/tests regardless of the player's #417 layout. */
  readonly slot: number;
  /** #352: last known cursor world position, or `null` if unknown. Computed
   *  once by the parent `ActionBar` (shared across every slot). */
  readonly cursorWorld: WorldPoint | null;
  /** #415: live camera-center getter, threaded down from `MatchScreen` (via
   *  `ActionBar`'s optional prop) so a click-to-cast's aim resolves through
   *  the SAME projection `InputSurface` uses — see `MapperContext`. */
  readonly getProjectionCenter: () => ProjectionCenter;
}): ReactElement {
  const { state, dispatch } = useStore();
  const client = useWsClient();
  // Read the LIVE keymap so a rebind updates the slot's keybind badge
  // immediately (was previously a module constant off DEFAULT_KEYMAP, so
  // rebinds never showed — #70 follow-up). Falls back to the 1-based slot
  // number when the slot is unbound. Keyed by the VISUAL slot — a keybind is
  // bound to a bar POSITION, not a fixed ability (#417).
  const { keymap } = useKeymap();
  const keybind = castSlotKeyLabel(keymap, slot) ?? String(slot + 1);
  // The LOCAL PLAYER'S actual picked champion (#244 `match.champion`
  // action), not a hardcoded placeholder — falls back to
  // DEFAULT_HUD_CHAMPION_ID only pre-pick / pre-match when `ownChampionId`
  // is still null (matches OwnFrame's existing fallback convention).
  const championId = state.ownChampionId ?? DEFAULT_HUD_CHAMPION_ID;
  // #417/#513/#530: every CONTENT/WIRE lookup below (name, icon, cooldown,
  // cast state, range, description, facts, target type, outgoing cast
  // intent) resolves against the CANONICAL ability slot the visual position
  // currently displays — never the visual position itself, which only
  // exists for keybind lookup / testid stability / drag-and-drop editing.
  // The single shared resolver (`abilityLayout.ts`) composes the persisted
  // layout permutation with the player's loadout filter exactly once; `null`
  // (no ability at this visual position) falls back to `-1`, a canonical
  // slot no champion ever has, so every downstream lookup renders this
  // position empty exactly like an out-of-range canonical slot always has.
  const canonicalSlot =
    resolveVisualSlotToCanonical(championId, state.ownLoadout, slot) ?? -1;
  const s = computeActionBarState(state.hudSnapshot, state.ownSeat, canonicalSlot, championId);
  const flashing = state.castFlashes.some((f) => f.slot === canonicalSlot);
  // #352: range-based keybind badge color — see the file header +
  // `computeRangeState` for the exact semantics.
  const rangeState = computeRangeState(
    state.hudSnapshot,
    state.ownSeat,
    canonicalSlot,
    championId,
    cursorWorld,
  );
  // #339: hover tracking for the ability tooltip. Local UI state only — never
  // touches the store/sim.
  const [hovered, setHovered] = useState(false);

  // #308 "just became ready" pop: detect the cooldown→ready transition and
  // remember when it happened. A plain ref (not state) — the HUD already
  // re-renders at 10 Hz (`hud.tick`), which is enough cadence to fade the
  // pop out; no timer of our own needed. Mutating a ref during render is
  // safe here (it never triggers an extra render and only ever reads its
  // OWN previous value), so the pop is visible starting the SAME render the
  // transition is observed in, not one sample later.
  const prevStateRef = useRef<ActionBarState["state"]>(s.state);
  const readyPoppedAtRef = useRef<number | null>(null);
  if (prevStateRef.current === "cooldown" && s.state === "ready") {
    readyPoppedAtRef.current = Date.now();
  }
  prevStateRef.current = s.state;
  const justReady =
    readyPoppedAtRef.current !== null && Date.now() - readyPoppedAtRef.current < READY_POP_MS;

  // #76: the caster's OWN current auras (already on the wire, #91's
  // `WireEntity.auras`) — the passive, read-only signal
  // `abilityLabelResolved`/`abilityIconLetterResolved` mirror the sim's
  // `holdsAura` condition against (e.g. Rogue's Backstab slot shows "Ambush"
  // while stealthed). Empty/tick-0 for a not-yet-connected snapshot — same
  // fallback shape every other `state.hudSnapshot?.` read here already uses.
  const selfAuras = ownEntity(state.hudSnapshot, state.ownSeat)?.auras ?? [];
  const selfTick = state.hudSnapshot?.tick ?? 0;

  const iconLetter = abilityIconLetterResolved(championId, canonicalSlot, selfAuras, selfTick);
  const empty = iconLetter === "";
  // Real icon art if assigned + bundled (#72); otherwise null -> letter glyph.
  const iconSrc = abilityIconSrc(championId, canonicalSlot);
  const borderColor = flashing
    ? "#e04040"
    : justReady
      ? "#7ee787"
      : s.state === "casting"
        ? "#d5a640"
        : s.state === "cooldown" || s.state === "gcd" || s.state === "charging"
          ? "#666"
          : empty
            ? "#2c343f"
            : "#5680d6";
  // #339: only a FILLED slot gets a tooltip — nothing to describe for a
  // reserved/empty one.
  const name = abilityLabelResolved(championId, canonicalSlot, selfAuras, selfTick);
  const showTooltip = hovered && !empty && name !== null;

  // #415: click-to-cast — a fallback alongside the keybind, on by default.
  // Funnels through the SAME `actionToIntent` translation a keypress uses
  // (`mapper.ts`), so aim/gating semantics (active-phase gate, skillshot aim
  // from the last known cursor position, etc.) never diverge between the two
  // input paths. A reserved/empty slot has no ability to cast — no-op.
  // #417: builds the action with the CANONICAL slot directly (already
  // resolved above) — no `resolveActionSlot` needed on this ctx.
  const handleCastClick = (): void => {
    if (empty) return;
    const ctx: MapperContext = {
      snapshot: state.hudSnapshot,
      // #731: never coerce an unknown seat to 0 (mirrors `InputSurface`'s
      // `buildContext`) — `castSlot` doesn't read `ownSeat` today, but this
      // keeps every `MapperContext` construction site honest about it.
      ownSeat: state.ownSeat,
      projectionCenter: getProjectionCenter(),
      keymap,
      lastPointer: getLastCanvasPoint(),
      // #815: same auto-acquire the keyboard path gets — clicking an
      // `enemy`-targetType ability with no valid target selects the nearest
      // enemy first, rather than firing a silent `noTarget` no-op. Resolves
      // against the CANONICAL slot this position displays (already resolved
      // above), so it can never judge a different ability than the one cast.
      resolveTargetType: (slot) => abilityTargetType(championId, slot as unknown as number),
    };
    const res = actionToIntent({ kind: "castSlot", slot: toAbilitySlot(canonicalSlot) }, ctx);
    // #795: no ability SFX on the click. Cast-time audio was SPECULATIVE — the
    // old comment here claimed it "fires on the accepted send, not on every
    // click (a rejected/gated click stays silent)", which was never true: an
    // accepted SEND is not an accepted CAST, so every cooldown/GCD/out-of-range
    // click still played the sound. Audio now fires on IMPACT, off the
    // snapshot's `damage` events (`sound/impactAudio.ts`).
    // #787: instead, report a locally-predicted failure through the SAME shared
    // predicate `InputSurface` uses (`input/castability.ts`) — never a
    // second, drifting copy of the rule. The send is unaffected.
    // #815: a click can resolve to TWO intents (auto-acquired `setTarget`,
    // then the cast). Order is load-bearing — `mapResultInputs` preserves it.
    for (const input of mapResultInputs(res)) {
      if (input.kind === "cast") {
        reportCastError(dispatch, {
          snapshot: state.hudSnapshot,
          ownSeat: state.ownSeat,
          championId,
          slot: canonicalSlot,
        });
      }
      client.sendInput(input);
    }
  };
  return (
    // Outer wrapper: hover tracking + the tooltip's positioning anchor. Kept
    // separate from the slot's own overflow-hidden box below so the tooltip
    // (which sits ABOVE the slot, outside its bounds) never gets clipped.
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {showTooltip ? (
        <AbilityTooltip
          testId={`action-slot-${slot}-tooltip`}
          name={name}
          description={abilityDescription(championId, canonicalSlot)}
          facts={abilityFacts(championId, canonicalSlot)}
        />
      ) : null}
      <div
        data-testid={`action-slot-${slot}`}
        data-state={s.state}
        data-slot={String(slot)}
        data-fraction={s.fraction.toFixed(3)}
        data-flash={flashing ? "true" : "false"}
        data-empty={empty ? "true" : "false"}
        data-just-ready={justReady ? "true" : "false"}
        data-range-state={rangeState}
        onClick={handleCastClick}
        style={{
          // #781: the camera's reserved bottom strip is derived from this slot
          // size + the bar's bottom inset (`render/hudLayout.ts`), so the two
          // read the SAME constants instead of a copy that can go stale.
          width: ACTION_BAR_SLOT_PX,
          height: ACTION_BAR_SLOT_PX,
          background: empty ? "#10151b" : "#161c24",
          border: `2px solid ${borderColor}`,
          borderRadius: 8,
          color: empty ? "#3a434f" : "#dfe6ef",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          cursor: empty ? "default" : "pointer",
          // #308: a brief outward pop on the cooldown→ready transition,
          // distinct from the rejection red-flash (that's a border-only cue;
          // this scales the whole slot momentarily).
          transform: justReady ? "scale(1.12)" : "scale(1)",
          transition: "transform 150ms ease-out",
          boxShadow: justReady ? "0 0 8px 2px rgba(126,231,135,0.65)" : "none",
        }}
      >
        <div
          data-testid={`action-slot-${slot}-fill`}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: `${(s.state === "ready" ? 0 : s.fraction) * 100}%`,
            background: s.state === "casting" ? "rgba(213,166,64,0.35)" : "rgba(120,120,120,0.35)",
          }}
        />
        {/* #308: darken the icon (translucent overlay, NOT a full-hide) while
         *  on cooldown, plus the numeric seconds-remaining readout. Sits above
         *  the icon/name (z-index 2) but below nothing else — there's no
         *  higher layer in this slot. #466: ALSO covers the "charging" state
         *  (a charge-based ability with >= 1 charge available but not at
         *  max) with the LIGHTER `partialChargeOverlay` treatment — "cooldown"
         *  (0 charges available, for a charge-based ability) keeps the exact
         *  same dark overlay an ordinary ability gets. */}
        {s.state === "cooldown" || s.state === "charging" ? (
          <div
            data-testid={`action-slot-${slot}-cooldown-overlay`}
            data-charge-shade={s.state === "charging" ? "partial" : "full"}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              background:
                s.state === "charging"
                  ? DEFAULT_COOLDOWN_OVERLAY_STYLE.partialChargeOverlay
                  : DEFAULT_COOLDOWN_OVERLAY_STYLE.darkOverlay,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <span
              data-testid={`action-slot-${slot}-cooldown-text`}
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#f2f4f8",
                textShadow: "0 1px 3px rgba(0,0,0,0.8)",
              }}
            >
              {s.remainingSeconds}
            </span>
          </div>
        ) : null}
        {/* #466: charge-count readout (bottom-right, white) — renders for
         *  ANY ability with a `charges` block (`maxCharges !== undefined`),
         *  regardless of the current overlay state (ready/cooldown/
         *  charging/gcd/casting all surface the live count). Font size is
         *  deliberately larger than the keybind badge's `fontSize: 10`
         *  below. Sits above the cooldown overlay (z-index 3) so it stays
         *  readable while darkened. */}
        {s.maxCharges !== undefined && s.chargesAvailable !== undefined ? (
          <span
            data-testid={`action-slot-${slot}-charge-count`}
            style={{
              position: "absolute",
              bottom: 2,
              right: 4,
              zIndex: 3,
              fontSize: DEFAULT_COOLDOWN_OVERLAY_STYLE.chargeCountFontSizePx,
              fontWeight: 700,
              color: DEFAULT_COOLDOWN_OVERLAY_STYLE.chargeCountColor,
              textShadow: "0 1px 3px rgba(0,0,0,0.8)",
              pointerEvents: "none",
            }}
          >
            {s.chargesAvailable}
          </span>
        ) : null}
        {/* Keybind badge (top-left). #352: color signals range — see the
         *  file header + `computeRangeState`. */}
        <div
          style={{
            position: "absolute",
            top: 2,
            left: 4,
            zIndex: 1,
            fontSize: 10,
            color: RANGE_STATE_COLOR[rangeState],
          }}
        >
          {keybind}
        </div>
        {/* Ability icon: real art when present (#72), else the letter glyph.
         *  256px source downscaled to the 56px slot via object-fit: contain. */}
        {iconSrc !== null ? (
          <img
            src={iconSrc}
            alt={name ?? ""}
            data-testid={`action-slot-${slot}-icon`}
            style={{
              position: "absolute",
              inset: 4,
              width: "calc(100% - 8px)",
              height: "calc(100% - 8px)",
              objectFit: "contain",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />
        ) : (
          <div
            data-testid={`action-slot-${slot}-name`}
            style={{
              zIndex: 1,
              fontSize: 8,
              fontWeight: 600,
              lineHeight: 1.2,
              textAlign: "center",
              padding: "0 2px",
              overflowWrap: "anywhere",
              hyphens: "auto",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
            }}
          >
            {name}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The always-leftmost auto-attack icon (#322) — NOT an ability slot: no
 * keybind badge (auto-attack isn't bound to a key), no cast/cooldown sweep
 * (the wire carries no auto-attack swing-timer state to the client, only
 * `Entity.autoCastState` server/replay-internal in `@pit/sim`). A distinct
 * amber border (vs. the ability slots' blue/grey/gold state colors) keeps it
 * visually unmistakable for a real, bindable ability slot.
 */
function AutoAttackSlot(): ReactElement {
  return (
    <div
      data-testid="action-slot-auto"
      style={{
        width: ACTION_BAR_SLOT_PX,
        height: ACTION_BAR_SLOT_PX,
        background: "#161c24",
        border: "2px solid #9a7b3c",
        borderRadius: 8,
        color: "#dfe6ef",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        data-testid="action-slot-auto-name"
        style={{
          fontSize: 8,
          fontWeight: 600,
          lineHeight: 1.2,
          textAlign: "center",
          padding: "0 2px",
          overflowWrap: "anywhere",
        }}
      >
        Auto Attack
      </div>
    </div>
  );
}

export interface ActionBarProps {
  /** #415: live camera-center getter (from `MatchScreen`, the same one
   *  `InputSurface` uses) — resolves a click-to-cast's skillshot aim through
   *  the SAME projection a keypress-with-mouse-aim uses. Optional so
   *  existing call sites/tests that render `<ActionBar />` bare keep working
   *  (falls back to `NO_OFFSET`, matching `projection.ts`'s own default). */
  readonly getProjectionCenter?: () => ProjectionCenter;
}

export function ActionBar({
  getProjectionCenter = () => NO_OFFSET,
}: ActionBarProps = {}): ReactElement {
  const { state } = useStore();
  const slots = Array.from({ length: ACTION_SLOT_COUNT }, (_, i) => i);
  // #352: last known cursor world position, computed ONCE per render (not
  // per-slot — every slot shares the same value) from the shared
  // `cursorTracker` singleton `InputSurface` writes to.
  const cursorPoint = getLastCanvasPoint();
  const cursorWorld: WorldPoint | null =
    cursorPoint === null ? null : screenToWorld(cursorPoint.x, cursorPoint.y, getProjectionCenter());
  return (
    <div
      data-testid="action-bar"
      style={{
        position: "absolute",
        // #781: shared with the camera's reserved bottom strip — see
        // `render/hudLayout.ts` (`HUD_BOTTOM_FOOTPRINT_PX` is this inset plus
        // one slot row, scaled by `HUD_SCALE`).
        bottom: ACTION_BAR_BOTTOM_INSET_PX,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        gap: 8,
        // #755: the bottom `HudScaleRegion` wrapper is `pointerEvents:
        // "none"` (it spans the viewport and must not eat world clicks
        // meant for `InputSurface`), and `pointer-events` inherits — so
        // click-to-cast (#415) re-enables it here. Same effective value
        // as the pre-#755 default; the slots' own hit boxes are scaled by
        // the wrapper's transform along with their visual boxes.
        pointerEvents: "auto",
      }}
    >
      <AutoAttackSlot />
      {slots.map((slot) => (
        <ActionSlot
          key={slot}
          slot={slot}
          cursorWorld={cursorWorld}
          getProjectionCenter={getProjectionCenter}
        />
      ))}
    </div>
  );
}
